// proceduralRelief.ts - Titan's relief nobody measured, drawn as real metres.
//
// Step 2 of the plan behind ARTIFICIAL FILL height on Titan: a pure function
// from a body-fixed DIRECTION (plus class weights, dune orientation and a
// seed) to a height in metres - no tile, level, uv or longitude scalar, so
// two callers reading the same direction always agree, cube-face seams
// included. Sampling the terrain/dune-direction maps and band-limiting to a
// tile's mesh spacing are later steps, layered on top of this one.
//
// Classes: plains (gentle fbm), dune (linear crests, Lorenz et al. 2006 /
// Lorenz and Radebaugh 2009), rugged (ridged fbm, capped per Radebaugh et al.
// 2007), lake/sea (exactly flat) and named craters (a fixed table, never
// from brightness).

import { clamp } from '../math/angles.ts';
import { directionForLatLon, type Vec3 } from './tileTree.ts';

const DEG = Math.PI / 180;

/**
 * The five classes the plan's imagery conditioning sorts Titan's ground into.
 * `ClassWeights` below carries the four soft, brightness-derived ones;
 * `crater` is not a weight because it never comes from brightness - it is a
 * fixed table of named features, applied as its own additive term.
 */
export type TerrainClassId = 'plains' | 'dune' | 'rugged' | 'lake' | 'crater';

/**
 * Soft per-point classification the baked class map yields at runtime: a
 * weight in [0, 1] for each brightness-derived class. They need not sum to 1
 * - a caller blending two neighbouring classes across a soft edge passes
 * whatever the smoothed map gives - but `lake` gates the whole field to
 * exactly flat regardless of what the others carry, so a point that is
 * mostly lake stays mostly flat.
 */
export interface ClassWeights {
  readonly plains: number;
  readonly dune: number;
  readonly rugged: number;
  readonly lake: number;
}

/** One IAU-named crater: Gazetteer centre and diameter, depth already scaled
 *  for Titan's shallow icy crust (the fixed table itself is baked, not this
 *  module's job to derive). Field names and `source` match `NamedCrater` in
 *  `src/world/types.ts` (arrives with the class-map PR) so a caller can hand
 *  this module a step-1 row unconverted. */
export interface NamedCrater {
  readonly name: string;
  /** Degrees, this repo's shipped-globe convention (east positive, -180..180). */
  readonly lat_deg: number;
  readonly lon_deg: number;
  readonly diameter_km: number;
  readonly depth_m: number;
  /** Where the centre, diameter and depth-scaling each came from. */
  readonly source: string;
}

/** One point to read the relief at. */
export interface ReliefSample {
  /** Unit body-fixed direction, the tileTree/tileMesh convention (z is the
   *  pole axis: `directionForLatLon`, `faceUvForDirection`). */
  readonly direction: Vec3;
  readonly classes: ClassWeights;
  /** Degrees clockwise from north (bearing), the crest LINE runs along; 90 is
   *  due east-west, the plan's cited default. Only meaningful where `dune` is
   *  nonzero, but always finite - a caller with no dune-direction map yet
   *  passes 90. */
  readonly duneOrientationDeg: number;
}

/** What the whole field needs beyond one sample: the body, the crater table
 *  and the switch the ARTIFICIAL FILL row drives. */
export interface ReliefConfig {
  readonly radiusKm: number;
  /** Keys the noise alongside the body and method, the way
   *  `fill_height.py`'s `_seed_u64` keys the crater population. */
  readonly seed: number;
  readonly craters: readonly NamedCrater[];
  /** The ARTIFICIAL FILL row's own switch: off, this returns 0 everywhere,
   *  the one frame `artificialRelief.ts`'s uniform-at-0 already draws. */
  readonly on: boolean;
}

/** True scale per Lorenz et al. 2006 (100 to 150 m high, Cassini RADAR):
 *  doubling for drama later is this one line. */
export const DUNE_CREST_HEIGHT_M = 100;
/** True scale per the same source (1 to 4 km apart; 3 km is its middle). */
export const DUNE_SPACING_M = 3000;

const DUNE_WIDTH_FRACTION = 0.45;
const DUNE_VARIATION = 0.25;
// "Smooth" means large next to the 3 km spacing: the crest line meanders
// gently over tens of km rather than jittering dune to dune, which is also
// what keeps a transect ALONG the crest (east-west, at the default
// orientation) close to flat over any span shorter than this wavelength.
const DUNE_WARP_WAVELENGTH_KM = 40;
const DUNE_WARP_AMPLITUDE_KM = 0.4;
const DUNE_VARIATION_WAVELENGTH_KM = 12;

const PLAINS_AMPLITUDE_M = 30;
const PLAINS_WAVELENGTH_KM = 6;

const RUGGED_CAP_M = 2000;
const RUGGED_WAVELENGTH_KM = 3;

/** Bowl and rim, in radii from a crater's centre - `fill_height.py`'s own
 *  shape constants, reused so a Titan crater and a Callisto one read alike. */
const BOWL_EDGE = 0.85;
const RIM_EDGE = 1.15;
const RIM_FRACTION = 0.25;

// dot() stays a Vec3 helper - craterReliefM reads it against a crater's own
// direction. Everything in the per-sample hot path below (plains, dune,
// rugged) takes plain x/y/z scalars instead: no per-call array allocation.
function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

// -- noise: an integer hash lattice sampled directly in body-fixed metres, so
// every field here is a function of x/y/z and never of a uv or a longitude
// scalar. Continuous by construction (trilinear interpolation of a hash
// lattice), and identical at every call for the same inputs (no Math.random,
// no Date.now). --

function hashU32(n: number): number {
  let x = n | 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
  x = Math.imul(x ^ (x >>> 13), 0x45d9f3b);
  x = x ^ (x >>> 16);
  return x >>> 0;
}

function latticeHash(ix: number, iy: number, iz: number, seed: number): number {
  const combined = (Math.imul(ix, 374761393) ^ Math.imul(iy, 668265263)
    ^ Math.imul(iz, 2147483647) ^ Math.imul(seed, 2654435761)) | 0;
  return (hashU32(combined) / 4294967295) * 2 - 1;
}

function smoothstep01(t: number): number {
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Value noise on a 3D lattice, one cell km on a side, in [-1, 1]. */
function valueNoise3(px: number, py: number, pz: number, seed: number): number {
  const x0 = Math.floor(px);
  const y0 = Math.floor(py);
  const z0 = Math.floor(pz);
  const sx = smoothstep01(px - x0);
  const sy = smoothstep01(py - y0);
  const sz = smoothstep01(pz - z0);
  const c000 = latticeHash(x0, y0, z0, seed);
  const c100 = latticeHash(x0 + 1, y0, z0, seed);
  const c010 = latticeHash(x0, y0 + 1, z0, seed);
  const c110 = latticeHash(x0 + 1, y0 + 1, z0, seed);
  const c001 = latticeHash(x0, y0, z0 + 1, seed);
  const c101 = latticeHash(x0 + 1, y0, z0 + 1, seed);
  const c011 = latticeHash(x0, y0 + 1, z0 + 1, seed);
  const c111 = latticeHash(x0 + 1, y0 + 1, z0 + 1, seed);
  const x00 = lerp(c000, c100, sx);
  const x10 = lerp(c010, c110, sx);
  const x01 = lerp(c001, c101, sx);
  const x11 = lerp(c011, c111, sx);
  const y0v = lerp(x00, x10, sy);
  const y1v = lerp(x01, x11, sy);
  return lerp(y0v, y1v, sz);
}

/** A tile's own mesh spacing fades an octave in over one octave-doubling: full
 *  weight once a wavelength spans at least 8 mesh cells, zero at or under 4,
 *  linear between - the plan's own band-limit formula. No `cellM` (a caller
 *  with no tile in play, or the unbanded craters/lake terms) is full weight. */
function octaveWeight(wavelengthM: number, cellM: number | undefined): number {
  if (cellM === undefined) return 1;
  return clamp((wavelengthM / cellM - 4) / 4, 0, 1);
}

/** Fractal sum of `valueNoise3`, in [-1, 1]: the gentle fields (plains, the
 *  dune warp and its spacing/height variation). Scalar x/y/z, not a `Vec3`,
 *  so a hot-path caller never allocates a position tuple just to pass it.
 *  `cellM`, when given, weights and skips octaves finer than the mesh can
 *  show; the remaining octaves are renormalized among themselves, so a coarse
 *  cell trims fine texture without dimming the large-scale shape it keeps. */
function fbm3(px: number, py: number, pz: number, seed: number, octaves: number,
  baseWavelengthKm: number, cellM?: number): number {
  let amp = 1;
  let freq = 1 / baseWavelengthKm;
  let sum = 0;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    const w = octaveWeight(1000 / freq, cellM);
    if (w > 0) {
      sum += amp * w * valueNoise3(px * freq, py * freq, pz * freq, seed + o * 101);
      norm += amp * w;
    }
    amp *= 0.5;
    freq *= 2.2;
  }
  return norm > 0 ? sum / norm : 0;
}

/** Ridged multifractal, in [0, 1]: mostly low with occasional sharp peaks,
 *  which is what "a few hundred metres, peaks near 2 km" needs. `cellM` bands
 *  it the same way `fbm3` does. */
function ridged3(px: number, py: number, pz: number, seed: number, octaves: number,
  baseWavelengthKm: number, cellM?: number): number {
  let amp = 1;
  let freq = 1 / baseWavelengthKm;
  let sum = 0;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    const w = octaveWeight(1000 / freq, cellM);
    if (w > 0) {
      const n = 1 - Math.abs(valueNoise3(px * freq, py * freq, pz * freq, seed + o * 131));
      sum += amp * w * n * n;
      norm += amp * w;
    }
    amp *= 0.55;
    freq *= 2.3;
  }
  return norm > 0 ? sum / norm : 0;
}

function plainsReliefM(px: number, py: number, pz: number, seed: number, cellM?: number): number {
  return fbm3(px, py, pz, seed ^ 0x9e3779b1, 3, PLAINS_WAVELENGTH_KM, cellM) * PLAINS_AMPLITUDE_M;
}

function ruggedReliefM(px: number, py: number, pz: number, seed: number, cellM?: number): number {
  return ridged3(px, py, pz, seed ^ 0x85ebca6b, 4, RUGGED_WAVELENGTH_KM, cellM) * RUGGED_CAP_M;
}

/**
 * Linear dune crests: `s`, the coordinate the ridge pattern repeats along, is
 * the great-circle angle away from the equator of the orientation-tilted
 * axis, in km - exactly latitude at the default orientation (90, due
 * east-west), turned by any other bearing. A crest LINE has no direction, so
 * bearing `d` and `d + 180` must give the same axis and the same `s`: the
 * axis is built from `cos(orientationDeg - 90)` times the z-component alone
 * (the tangent-plane term a naive axis vector would add is always exactly
 * perpendicular to the sample direction, so it cannot move `s` regardless of
 * bearing - the closed form here is that same identity, not a new field),
 * and `cos` is 180-antiperiodic (`cos(x + pi) = -cos(x)`) - so `s` itself
 * flips sign exactly under `orientationDeg -> orientationDeg + 180`, for
 * every bearing, continuously, no branch.
 *
 * A smooth 3D warp (`fbm3`, band-limited, no seam, and independent of
 * `orientationDeg`) offsets the underlying z-component before that same
 * `cos` scaling - the meander that makes the spacing between crests look
 * uneven rather than a ruler grid - and a second low-frequency noise read
 * varies the crest height by `DUNE_VARIATION`. Because the warp sits inside
 * the same 180-antiperiodic construction as the bearing, `s + warp` flips
 * sign along with `s`, not independently of it, so the crest profile only
 * has to be even in `s` (crests centred on `s = 0 mod spacing`, not offset
 * into each period) for the whole field to be exactly invariant under the
 * 180-degree relabelling. The period itself, `DUNE_SPACING_M`, stays fixed:
 * `s` grows with latitude away from the equator, so dividing it by a
 * position-varying spacing would amplify that noise by however many periods
 * away from the equator a point sits, which is exactly the kind of hidden
 * direction-dependence this module exists to not have.
 */
function duneReliefM(
  directionZ: number,
  px: number,
  py: number,
  pz: number,
  radiusKm: number,
  orientationDeg: number,
  seed: number,
  cellM?: number,
): number {
  // The crest is one wavelength, DUNE_SPACING_M: banded as a single term
  // rather than octave by octave, since it is not an fbm sum.
  const duneWeight = octaveWeight(DUNE_SPACING_M, cellM);
  if (duneWeight <= 0) return 0;
  const warpKm = fbm3(px, py, pz, seed ^ 0xc2b2ae35, 2, DUNE_WARP_WAVELENGTH_KM)
    * DUNE_WARP_AMPLITUDE_KM;
  const dzWarped = directionZ + warpKm / radiusKm;
  const delta = (orientationDeg - 90) * DEG;
  const s = Math.asin(clamp(Math.cos(delta) * dzWarped, -1, 1)) * radiusKm;
  const vx = px / DUNE_VARIATION_WAVELENGTH_KM;
  const vy = py / DUNE_VARIATION_WAVELENGTH_KM;
  const vz = pz / DUNE_VARIATION_WAVELENGTH_KM;
  const heightNoise = valueNoise3(vx, vy, vz, seed ^ 0x165667b1);
  const spacingKm = DUNE_SPACING_M / 1000;
  const phase = s / spacingKm;
  // Even in `s`: a crest is centred on every multiple of `spacingKm`, not
  // offset to the start of each period, so `shape(-s) === shape(s)` exactly.
  const centered = phase - Math.round(phase);
  const halfWidth = DUNE_WIDTH_FRACTION / 2;
  const shape = Math.abs(centered) < halfWidth
    ? 0.5 * (1 + Math.cos((Math.PI * centered) / halfWidth))
    : 0;
  return duneWeight * shape * DUNE_CREST_HEIGHT_M * (1 + DUNE_VARIATION * heightNoise);
}

/** One crater's bowl-and-rim profile, `u` in crater radii from its centre:
 *  a flat floor to `BOWL_EDGE` (Titan's craters are shallow enough that
 *  nearly all of them are past the simple/complex transition, per
 *  `fill_height.py`'s own crater_depth_km), a smoothstep wall up to a rim at
 *  1 radius, and the rim tapering to 0 by `RIM_EDGE`. Continuous at both
 *  seams: the wall and the taper each start exactly where the piece before
 *  them ends. */
function craterBowlM(u: number, depthM: number): number {
  if (u >= RIM_EDGE) return 0;
  const rimM = RIM_FRACTION * depthM;
  if (u <= BOWL_EDGE) return -depthM;
  if (u <= 1) {
    const t = smoothstep01((u - BOWL_EDGE) / (1 - BOWL_EDGE));
    return lerp(-depthM, rimM, t);
  }
  const t = smoothstep01((u - 1) / (RIM_EDGE - 1));
  return lerp(rimM, 0, t);
}

function craterReliefM(direction: Vec3, radiusKm: number, craters: readonly NamedCrater[]): number {
  let total = 0;
  for (const crater of craters) {
    const craterRadiusKm = crater.diameter_km / 2;
    if (craterRadiusKm <= 0) continue;
    const craterDir = directionForLatLon(crater.lat_deg * DEG, crater.lon_deg * DEG);
    const cosAngle = clamp(dot(direction, craterDir), -1, 1);
    const cutoffRad = (RIM_EDGE * craterRadiusKm) / radiusKm;
    // Cheap early-out: acos is monotonic decreasing, so a dot product under
    // the cutoff's cosine is an angle past RIM_EDGE without paying for acos.
    if (cutoffRad < Math.PI && cosAngle < Math.cos(cutoffRad)) continue;
    const angleKm = Math.acos(cosAngle) * radiusKm;
    total += craterBowlM(angleKm / craterRadiusKm, crater.depth_m);
  }
  return total;
}

/**
 * The relief at one point, metres, positive up.
 *
 * `config.on` false (the ARTIFICIAL FILL row switched off) returns exactly 0
 * with no other read - the same frame the uniform-at-0 chunks already draw.
 * Otherwise: the four brightness classes blend by their own weights, craters
 * add atop that blend, and the whole sum is scaled by `1 - lake`, so a point
 * that is entirely lake is exactly 0 regardless of what noise or craters
 * would otherwise have put there.
 *
 * `cellM`, when given, is the calling tile's own mesh spacing in metres: each
 * class term's octaves fade out below the plan's band-limit ratio, so a
 * coarse tile shows only what its own lattice could represent. Omitted, every
 * octave is full weight - today's output exactly. Craters are never banded:
 * their shape is a fixed table, not noise, and a rim has to read the same at
 * any level the way the surface clamp already assumes.
 */
export function reliefHeightM(sample: ReliefSample, config: ReliefConfig, cellM?: number): number {
  if (!config.on) return 0;
  const [ux, uy, uz] = sample.direction;
  const px = ux * config.radiusKm;
  const py = uy * config.radiusKm;
  const pz = uz * config.radiusKm;
  const c = sample.classes;
  let base = 0;
  if (c.plains > 0) base += c.plains * plainsReliefM(px, py, pz, config.seed, cellM);
  if (c.dune > 0) {
    base += c.dune * duneReliefM(
      uz, px, py, pz, config.radiusKm, sample.duneOrientationDeg, config.seed, cellM);
  }
  if (c.rugged > 0) base += c.rugged * ruggedReliefM(px, py, pz, config.seed, cellM);
  const craterM = config.craters.length > 0
    ? craterReliefM(sample.direction, config.radiusKm, config.craters)
    : 0;
  const nonLake = 1 - clamp(c.lake, 0, 1);
  return nonLake * (base + craterM);
}
